import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth, ROLE_HOME_PATHS } from '../../contexts/AuthContext';
import PoweredByFooter from '../ui/PoweredByFooter';
import { cn } from '../../lib/utils';

const loginSchema = Yup.object({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

export default function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && user?.role) {
      const homePath = ROLE_HOME_PATHS[user.role] || '/';
      navigate(homePath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    try {
      const result = await login(values.username, values.password);
      const redirectTo = location.state?.from?.pathname || result.homePath || '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — decorative gradient hero, aria-hidden */}
      <div
        aria-hidden="true"
        className="hidden md:flex md:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex-col justify-center items-center p-12"
      >
        <div className="text-center">
          <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center mb-8 mx-auto">
            <span className="text-white font-bold text-4xl">F</span>
          </div>
          <p className="text-5xl font-bold text-white mb-3">FMPS</p>
          <p className="text-white/60 text-sm tracking-widest uppercase">Fare Management & Payment System</p>
        </div>
      </div>

      {/* Right Panel — accessible content */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Branding — always in DOM for accessibility */}
          <div className="text-center mb-8">
            <p className="text-2xl font-bold text-primary-600 mb-1">FMPS</p>
            <p className="text-sm text-on-surface-variant">Fare Management & Payment System</p>
          </div>

          <h2 className="text-2xl font-bold text-on-surface mb-1">Welcome Back</h2>
          <p className="text-on-surface-variant mb-8">Sign in to your account to continue</p>

          {error && (
            <div className="bg-error-light border border-error/20 text-error px-4 py-3 rounded-md mb-6 text-sm">
              {error}
            </div>
          )}

          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="space-y-5">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-on-surface mb-1.5">
                    Username
                  </label>
                  <Field
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Enter your username"
                    className={cn(
                      'w-full px-4 py-2.5 rounded-md border bg-surface text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
                      errors.username && touched.username ? 'border-error' : 'border-outline'
                    )}
                  />
                  {errors.username && touched.username && (
                    <p className="text-error text-xs mt-1">{errors.username}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-on-surface mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Field
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className={cn(
                        'w-full px-4 py-2.5 pr-11 rounded-md border bg-surface text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
                        errors.password && touched.password ? 'border-error' : 'border-outline'
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <p className="text-error text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white py-2.5 rounded-md font-semibold hover:bg-primary-500 active:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>
              </Form>
            )}
          </Formik>

          <div className="mt-8">
            <PoweredByFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
